import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import SuccessToast from '../../../components/ui/SuccessToast';

const statusColors = {
  submitted: { label: 'Submitted', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  under_review: { label: 'Under Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  approved: { label: 'Approved', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  rejected: { label: 'Rejected', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  draft: { label: 'Draft', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
};

const DriverReviewQueue = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: '', subMessage: '', variant: 'success' });

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('driver_onboarding_applications')
        ?.select('*')
        ?.in('status', ['submitted', 'under_review'])
        ?.order('submitted_at', { ascending: true });
      if (!error) setApplications(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const showToast = (message, subMessage, variant = 'success') => {
    setToast({ visible: true, message, subMessage, variant });
  };

  // Fix 3 & 4: Approve driver — sets driver_profiles.is_verified = true via user_id link
  const handleApprove = async (application) => {
    if (!application?.user_id) {
      showToast('Cannot approve application', 'Driver must have a linked user account', 'warning');
      return;
    }
    setProcessing(application?.id);
    try {
      const approvedAt = new Date();
      // Update onboarding application status to approved
      const { error: appError } = await supabase
        ?.from('driver_onboarding_applications')
        ?.update({ status: 'approved', reviewed_at: approvedAt?.toISOString() })
        ?.eq('id', application?.id);
      if (appError) throw appError;

      // Sync: set driver_profiles.is_verified = true via user_id (Fix 4 link)
      const { error: profileError } = await supabase
        ?.from('driver_profiles')
        ?.update({ is_verified: true })
        ?.eq('user_id', application?.user_id);
      if (profileError) throw profileError;

      const formattedTime = approvedAt?.toLocaleString('en-KE', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
      showToast(`${application?.full_name} approved`, `Approved at ${formattedTime}`, 'success');
      fetchApplications();
    } catch (e) {
      console.error(e);
      showToast('Approval failed', e?.message, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (application) => {
    setProcessing(application?.id);
    try {
      const rejectedAt = new Date();
      const { error } = await supabase
        ?.from('driver_onboarding_applications')
        ?.update({ status: 'rejected', reviewed_at: rejectedAt?.toISOString() })
        ?.eq('id', application?.id);
      if (error) throw error;
      const formattedTime = rejectedAt?.toLocaleString('en-KE', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
      showToast(`${application?.full_name} rejected`, `Rejected at ${formattedTime}`, 'error');
      fetchApplications();
    } catch (e) {
      console.error(e);
      showToast('Rejection failed', e?.message, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkUnderReview = async (applicationId) => {
    try {
      await supabase
        ?.from('driver_onboarding_applications')
        ?.update({ status: 'under_review' })
        ?.eq('id', applicationId);
      fetchApplications();
    } catch (e) { console.error(e); }
  };

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="bg-surface rounded-xl border border-border p-5">
      <SuccessToast
        visible={toast?.visible}
        message={toast?.message}
        subMessage={toast?.subMessage}
        onDismiss={() => setToast(t => ({ ...t, visible: false }))}
        duration={4000}
        position="top"
        variant={toast?.variant}
      />
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(96,165,250,0.15)' }}>
            <Icon name="UserCheck" size={18} style={{ color: '#60a5fa' }} />
          </div>
          <div>
            <h3 className="text-text-primary font-bold text-base">Driver Applications</h3>
            <p className="text-text-secondary text-xs">Pending review queue</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>
            {applications?.length} pending
          </span>
          <button onClick={fetchApplications} className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors" title="Refresh">
            <Icon name="RefreshCw" size={14} className="text-text-secondary" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="h-20 rounded-xl animate-pulse bg-border" />
          ))}
        </div>
      ) : applications?.length === 0 ? (
        <div className="text-center py-10">
          <div className="text-4xl mb-2">✅</div>
          <p className="text-text-primary font-semibold text-sm">No pending applications</p>
          <p className="text-text-secondary text-xs mt-1">All driver applications have been reviewed</p>
        </div>
      ) : (
        <div className="space-y-3">
          {applications?.map(app => {
            const statusCfg = statusColors?.[app?.status] || statusColors?.draft;
            const isExpanded = expandedId === app?.id;
            const isProcessing = processing === app?.id;

            return (
              <div key={app?.id} className="rounded-xl border border-border overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)' }}>
                {/* Row */}
                <div className="flex items-center gap-3 p-4">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white" style={{ background: 'rgba(96,165,250,0.2)' }}>
                    {app?.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-text-primary font-semibold text-sm">{app?.full_name}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: statusCfg?.bg, color: statusCfg?.color }}>{statusCfg?.label}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-text-secondary flex-wrap">
                      <span>{app?.phone}</span>
                      <span>·</span>
                      <span className="capitalize">{app?.vehicle_type}</span>
                      {app?.plate_number && <><span>·</span><span>{app?.plate_number}</span></>}
                      <span>·</span>
                      <span>Submitted {formatDate(app?.submitted_at || app?.created_at)}</span>
                    </div>
                  </div>
                  <button onClick={() => setExpandedId(isExpanded ? null : app?.id)} className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors flex-shrink-0">
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-text-secondary" />
                  </button>
                </div>

                {/* Expanded details + actions */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-text-secondary">Email:</span> <span className="text-text-primary">{app?.email || '—'}</span></div>
                      <div><span className="text-text-secondary">National ID:</span> <span className="text-text-primary">{app?.national_id || '—'}</span></div>
                      <div><span className="text-text-secondary">Vehicle Model:</span> <span className="text-text-primary">{app?.vehicle_model || '—'}</span></div>
                      <div><span className="text-text-secondary">Vehicle Year:</span> <span className="text-text-primary">{app?.vehicle_year || '—'}</span></div>
                      <div><span className="text-text-secondary">ID Verification:</span> <span className="text-text-primary capitalize">{app?.id_verification_status || '—'}</span></div>
                      <div><span className="text-text-secondary">Terms Accepted:</span> <span className={app?.terms_accepted ? 'text-[#34d399]' : 'text-[#f87171]'}>{app?.terms_accepted ? 'Yes' : 'No'}</span></div>
                      <div><span className="text-text-secondary">Linked Account:</span> <span className={app?.user_id ? 'text-[#34d399]' : 'text-[#fbbf24]'}>{app?.user_id ? 'Linked ✓' : 'Not linked'}</span></div>
                      {app?.reviewer_notes && (
                        <div className="col-span-2"><span className="text-text-secondary">Notes:</span> <span className="text-text-primary">{app?.reviewer_notes}</span></div>
                      )}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {app?.status === 'submitted' && (
                        <button onClick={() => handleMarkUnderReview(app?.id)} disabled={isProcessing}
                          className="px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                          style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>
                          <Icon name="Eye" size={13} className="inline mr-1" />Mark Under Review
                        </button>
                      )}
                      <button onClick={() => handleApprove(app)} disabled={isProcessing || !app?.user_id}
                        className="px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                        style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}
                        title={!app?.user_id ? 'Driver must have a linked user account to approve' : ''}>
                        {isProcessing ? (
                          <span className="flex items-center gap-1"><Icon name="Loader" size={13} className="animate-spin" />Processing...</span>
                        ) : (
                          <span className="flex items-center gap-1"><Icon name="CheckCircle" size={13} />Approve & Verify</span>
                        )}
                      </button>
                      <button onClick={() => handleReject(app)} disabled={isProcessing}
                        className="px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                        style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
                        <Icon name="XCircle" size={13} className="inline mr-1" />Reject
                      </button>
                    </div>
                    {!app?.user_id && (
                      <p className="text-xs" style={{ color: '#fbbf24' }}>
                        ⚠️ This application is not linked to a user account. The driver must complete registration before approval can set their verified status.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DriverReviewQueue;
